import React from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { generateDateTicks } from '../../../../services/dataTicks';

const FuelConsumptionRPM = ({ data }) => {
  console.log("DataFuelVsRpm:", data);

  // Filter out dates with zero data and prepare chart data
  const validData = data.filter(item => item.dataPointCount > 0);
  
  const chartData = validData.map(item => ({
    date: item.date,
    rpm: Number(item.rpm) || 0,
    consumption: (item.consumption || 0)
  }));

  const dates = chartData.length > 0 ? chartData.map(item => item.date) : [];
  
  let tickPositions = generateDateTicks(dates);

  // Prepare chart series data - both as scatter
  const rpmSeries = chartData.map(item => [
    new Date(item.date).getTime(),
    item.rpm
  ]);

  const consumptionSeries = chartData.map(item => [
    new Date(item.date).getTime(),
    item.consumption
  ]);

  // Calculate trend line only for valid data points
  const calculateTrendLine = (seriesData) => {
    if (seriesData.length < 2) return [];

    // Use day indices instead of timestamps to avoid precision issues
    const n = seriesData.length;
    let sumX = 0, sumY = 0, sumXY = 0, sumX2 = 0;

    seriesData.forEach((point, index) => {
      const x = index; // Use day index instead of timestamp
      const y = point[1]; // value
      sumX += x;
      sumY += y;
      sumXY += x * y;
      sumX2 += x * x;
    });

    // Calculate slope (m) and intercept (b) for y = mx + b
    const m = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
    const b = (sumY - m * sumX) / n;

    // Create trend line points using original timestamps
    return seriesData.map((point, index) => [
      point[0], // original timestamp
      m * index + b // calculated y value
    ]);
  };

  // Calculate combined trend line that represents the relationship
  const calculateCombinedTrendLine = () => {
    if (chartData.length < 2) return [];

    // Use consumption trend as the primary trend since it's the main metric
    const consumptionTrend = calculateTrendLine(consumptionSeries);
    
    return consumptionTrend;
  };

  const combinedTrendData = calculateCombinedTrendLine();

  const chartOptions = {
    chart: {
      type: 'scatter',
      height: '500px',
      backgroundColor: 'transparent',
      spacing: [20, 15, 15, 15]
    },
    title: {
      text: null
    },
    xAxis: {
      type: 'datetime',
      tickPositions: tickPositions,
      title: {
        text: null,
      },
      labels: {
        format: '{value:%b %e}',
        style: {
          color: '#666666'
        }
      },
      gridLineWidth: 1,
      gridLineColor: '#f0f0f0',
    },
    yAxis: [
      {
        title: {
          text: 'Avg ME Consumption (MT/day)',
          style: {
            color: '#7d3c98',
            fontSize: '12px'
          }
        },
        min: 0,
        gridLineWidth: 1,
        gridLineColor: '#f0f0f0',
        labels: {
          style: {
            color: '#666666'
          }
        },
        opposite: false // Consumption on left side
      },
      {
        title: {
          text: 'Avg Prop RPM (rpm)',
          style: {
            color: '#27ae60',
            fontSize: '12px'
          }
        },
        min: 0,
        max: 130,
        tickInterval: 10,
        gridLineWidth: 0,
        labels: {
          style: {
            color: '#666666'
          }
        },
        opposite: true // RPM on right side
      }
    ],
    legend: {
      align: 'right',
      verticalAlign: 'top',
      itemStyle: {
        color: '#666666',
        fontWeight: 'normal'
      },
      itemHoverStyle: {
        color: '#333333'
      },
      symbolRadius: 0
    },
    tooltip: {
      shared: true,
      crosshairs: true,
      backgroundColor: '#ffffff',
      borderColor: '#e5e7eb',
      borderRadius: 6,
      shadow: true,
      useHTML: true,
      headerFormat:
        '<div style="font-weight:600;font-size:12px;margin-bottom:5px">{point.x:%b %e, %Y}</div>',
      pointFormat:
        '<div style="display:flex;justify-content:space-between"><span style="color:{series.color}">{series.name}:</span><span style="font-weight:700;margin-left:10px">{point.y}</span></div>',
    },
    plotOptions: {
      scatter: {
        marker: {
          radius: 5,
          symbol: 'circle',
          lineWidth: 2,
          lineColor: '#ffffff',
          states: {
            hover: {
              enabled: true,
              lineColor: '#333333',
              radius: 6,
            },
          },
        },
        states: {
          hover: {
            halo: {
              size: 8,
            },
          },
        },
      },
      line: {
        marker: {
          enabled: false,
        },
        states: {
          hover: {
            halo: {
              size: 0,
            },
          },
        },
      },
    },
    series: [
      {
        name: 'Fuel Consumption',
        type: 'scatter',
        data: consumptionSeries,
        color: '#7d3c98',
        yAxis: 0,
        marker: {
          fillColor: '#7d3c98',
          lineWidth: 2,
          lineColor: '#ffffff',
          radius: 5,
        },
        tooltip: {
          valueSuffix: ' MT/day',
        },
      },
      {
        name: 'Propeller RPM',
        type: 'scatter',
        data: rpmSeries,
        color: '#27ae60',
        yAxis: 1,
        marker: {
          fillColor: '#27ae60',
          lineWidth: 2,
          lineColor: '#ffffff',
          radius: 5,
        },
        tooltip: {
          valueSuffix: ' rpm',
        },
      },
      {
        name: 'Performance Trend',
        type: 'line',
        data: combinedTrendData,
        color: '#e74c3c',
        yAxis: 0,
        lineWidth: 2,
        dashStyle: 'Dash',
        marker: {
          enabled: false,
        },
        states: {
          hover: {
            lineWidth: 3,
          },
        },
        tooltip: {
          valueSuffix: ' MT/day',
          pointFormat: '<div style="display:flex;justify-content:space-between"><span style="color:{series.color}">{series.name}:</span><span style="font-weight:700;margin-left:10px">{point.y:.2f}</span></div>'
        },
      }
    ],
    credits: {
      enabled: false
    },
    responsive: {
      rules: [{
        condition: {
          maxWidth: 600
        },
        chartOptions: {
          legend: {
            align: 'center',
            verticalAlign: 'bottom',
            layout: 'horizontal'
          },
          xAxis: {
            labels: {
              format: '{value:%b %e}',
            },
          },
        }
      }]
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm">
      <div className="flex justify-between items-center">
        <h1>Fuel Consumption & RPM over Time</h1>
      </div>

      {chartData.length > 0 ? (
        <HighchartsReact
          highcharts={Highcharts}
          options={chartOptions}
          containerProps={{
            className: 'w-full',
            style: { height: '500px' }
          }}
        />
      ) : (
        <div className="flex items-center justify-center h-96">
          <p className="text-gray-500 text-lg">No data available for the selected period</p>
        </div>
      )}
    </div>
  );
};

export default FuelConsumptionRPM;